import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2, Check, X, Loader2, Users, Play, Pause, Volume2, Image as ImageIcon } from 'lucide-react';
import './CharacterDetailPage.css';
import AuthenticatedLayout from './components/AuthenticatedLayout';
import { updateCharacter, deleteCharacter, getOrgAssets } from './lib/api';
import { useAuthContext } from './components/AuthProvider';
import {
  useCharactersStore,
  useCharacterDetail,
  fetchCharacterDetailIfStale,
  useCharacterDetailsStore,
} from './stores';

function CharacterDetailPage() {
  const { characterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const updateCharacterInStore = useCharactersStore((s) => s.updateCharacter);
  const deleteCharacterFromStore = useCharactersStore((s) => s.deleteCharacter);
  const setDetailEntry = useCharacterDetailsStore((s) => s.setEntry);
  const removeDetailEntry = useCharacterDetailsStore((s) => s.removeEntry);
  const { data: character, hasCache: hasDetailCache } = useCharacterDetail(characterId, user?.id);

  const isLoading = !hasDetailCache;

  const [error, setError] = useState(null);
  const setCharacter = useCallback(
    (next) => {
      if (next) setDetailEntry(characterId, next);
    },
    [characterId, setDetailEntry]
  );

  // Inline name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  // Description editing (stored as the character's personality metadata)
  const [editingPersonality, setEditingPersonality] = useState(false);
  const [personalityValue, setPersonalityValue] = useState('');
  const [isSavingPersonality, setIsSavingPersonality] = useState(false);

  // Physical appearance editing
  const [editingAppearance, setEditingAppearance] = useState(false);
  const [appearanceValue, setAppearanceValue] = useState('');
  const [isSavingAppearance, setIsSavingAppearance] = useState(false);

  // Asset selection
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [orgAssets, setOrgAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isSavingAsset, setIsSavingAsset] = useState(false);

  // Voice preview
  const previewAudioRef = useRef(null);
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch (or revalidate) the character. Cached entries render instantly while this runs in the background.
  useEffect(() => {
    if (!characterId) return;
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        await fetchCharacterDetailIfStale({ id: characterId, userId: user?.id });
      } catch (err) {
        if (!cancelled) setError('Failed to load character');
      }
    })();
    return () => { cancelled = true; };
  }, [characterId, user?.id]);

  // Seed inline-edit fields once per character id (don't clobber user's in-progress edits on revalidation).
  const seededForIdRef = useRef(null);
  useEffect(() => {
    if (!character || seededForIdRef.current === characterId) return;
    seededForIdRef.current = characterId;
    setNameValue(character.name || '');
    setPersonalityValue(character.personality || '');
    setAppearanceValue(character.physicalAppearance || '');
  }, [character, characterId]);

  useEffect(() => () => {
    previewAudioRef.current?.pause();
  }, []);

  const toggleVoicePreview = useCallback(() => {
    const previewUrl = character?.voicePreviewUrl;
    if (!previewUrl) return;

    if (isVoicePlaying && previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setIsVoicePlaying(false);
      return;
    }

    previewAudioRef.current?.pause();
    const audio = new Audio(previewUrl);
    audio.onended = () => setIsVoicePlaying(false);
    audio.onerror = () => setIsVoicePlaying(false);
    previewAudioRef.current = audio;
    setIsVoicePlaying(true);
    audio.play().catch(() => setIsVoicePlaying(false));
  }, [character?.voicePreviewUrl, isVoicePlaying]);

  const handleSaveName = async () => {
    if (!nameValue.trim() || nameValue === character.name) {
      setEditingName(false);
      return;
    }
    try {
      setIsSavingName(true);
      const resp = await updateCharacter(characterId, { name: nameValue.trim() });
      if (resp.character) {
        setCharacter(resp.character);
        updateCharacterInStore(characterId, { name: resp.character.name });
      }
      setEditingName(false);
    } catch (err) {
      setError('Failed to save name');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleSavePersonality = async () => {
    if (personalityValue === (character.personality || '')) {
      setEditingPersonality(false);
      return;
    }
    try {
      setIsSavingPersonality(true);
      const resp = await updateCharacter(characterId, { personality: personalityValue.trim() });
      if (resp.character) {
        setCharacter(resp.character);
        updateCharacterInStore(characterId, { personality: resp.character.personality });
      }
      setEditingPersonality(false);
    } catch (err) {
      setError('Failed to save personality');
    } finally {
      setIsSavingPersonality(false);
    }
  };

  const handleSaveAppearance = async () => {
    if (appearanceValue === (character.physicalAppearance || '')) {
      setEditingAppearance(false);
      return;
    }
    try {
      setIsSavingAppearance(true);
      const resp = await updateCharacter(characterId, { physicalAppearance: appearanceValue.trim() });
      if (resp.character) {
        setCharacter(resp.character);
        updateCharacterInStore(characterId, { physicalAppearance: resp.character.physicalAppearance });
      }
      setEditingAppearance(false);
    } catch (err) {
      setError('Failed to save appearance');
    } finally {
      setIsSavingAppearance(false);
    }
  };

  const handleOpenAssetPicker = async () => {
    setShowAssetPicker(true);
    if (orgAssets.length === 0) {
      try {
        setIsLoadingAssets(true);
        const resp = await getOrgAssets();
        setOrgAssets(resp.assets || []);
      } catch (err) {
        setError('Failed to load assets');
      } finally {
        setIsLoadingAssets(false);
      }
    }
  };

  const handleSelectAsset = async (asset) => {
    try {
      setIsSavingAsset(true);
      const resp = await updateCharacter(characterId, { assetId: asset?.id || null });
      if (resp.character) {
        setCharacter(resp.character);
        updateCharacterInStore(characterId, {
          assetId: resp.character.assetId,
          assetImageUrl: resp.character.assetImageUrl,
        });
      }
      setShowAssetPicker(false);
    } catch (err) {
      setError('Failed to update asset');
    } finally {
      setIsSavingAsset(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteCharacter(characterId);
      deleteCharacterFromStore(characterId);
      removeDetailEntry(characterId);
      navigate('/characters');
    } catch (err) {
      setError('Failed to delete character');
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout>
        <div className="character-detail-loading">Loading character...</div>
      </AuthenticatedLayout>
    );
  }

  if (error && !character) {
    return (
      <AuthenticatedLayout>
        <div className="character-detail-error-page">
          <p>{error}</p>
          <button onClick={() => navigate('/characters')}>Back to Characters</button>
        </div>
      </AuthenticatedLayout>
    );
  }

  if (!character) return null;

  const isPlatform = !character.userId;
  const characterImageUrl = character.assetImageUrl || character.imageUrl || character.asset?.imageUrl;
  const hasVoicePreview = Boolean(character.voicePreviewUrl);

  return (
    <AuthenticatedLayout>
      <div className="character-detail-content">
        <div className="character-detail-shell">
          <header className="character-detail-topbar">
            <button className="character-detail-back" onClick={() => navigate('/characters')}>
              <ArrowLeft size={15} />
              <span>Characters</span>
            </button>
            <span className="character-detail-breadcrumb-separator">/</span>
            <span className="character-detail-breadcrumb-current">{character.name || 'Unnamed'}</span>
          </header>

          {error && <div className="character-detail-error" role="alert">{error}</div>}

          <div className="character-detail-layout">
            <main className="character-detail-main">
              <div className="character-detail-visual-header">
                <div>
                  <span className="character-detail-section-kicker">Appearance</span>
                  <p className="character-detail-visual-hint">
                    The primary visual reference used across generations.
                  </p>
                </div>
                {!isPlatform && (
                  <button className="character-detail-change-image" onClick={handleOpenAssetPicker}>
                    <ImageIcon size={14} /> Change image
                  </button>
                )}
              </div>

              <button
                type="button"
                className={`character-detail-hero ${isPlatform ? 'character-detail-hero--static' : ''}`}
                onClick={!isPlatform ? handleOpenAssetPicker : undefined}
                disabled={isPlatform}
                aria-label={isPlatform ? `${character.name || 'Character'} appearance` : 'Change character appearance image'}
              >
                {characterImageUrl ? (
                  <img className="character-detail-hero-img" src={characterImageUrl} alt={character.name || 'Character'} />
                ) : (
                  <div className="character-detail-hero-placeholder">
                    <Users size={56} />
                    <span>No appearance image</span>
                  </div>
                )}
                {!isPlatform && (
                  <span className="character-detail-hero-overlay" aria-hidden="true">
                    <span className="character-detail-hero-overlay-action">
                      <Pencil size={15} /> Replace appearance
                    </span>
                  </span>
                )}
              </button>
            </main>

            <aside className="character-detail-sidebar" aria-label="Character details">
              <div className="character-detail-identity">
                <div className="character-detail-identity-meta">
                  <span className="character-detail-section-kicker">Character profile</span>
                  <span className="character-detail-source-badge">{isPlatform ? 'Library' : 'Your character'}</span>
                </div>

                <div className="character-detail-field character-detail-name-field">
                  {!isPlatform && editingName ? (
                    <div className="character-detail-inline-edit">
                      <input
                        className="character-detail-inline-input character-detail-name-input"
                        value={nameValue}
                        onChange={(e) => setNameValue(e.target.value)}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName();
                          if (e.key === 'Escape') { setEditingName(false); setNameValue(character.name || ''); }
                        }}
                      />
                      <button className="character-detail-inline-btn" onClick={handleSaveName} disabled={isSavingName} aria-label="Save name">
                        {isSavingName ? <Loader2 size={14} className="spin" /> : <Check size={14} />}
                      </button>
                      <button className="character-detail-inline-btn" onClick={() => { setEditingName(false); setNameValue(character.name || ''); }} aria-label="Cancel name edit">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="character-detail-name-row">
                      <h1 className="character-detail-name">{character.name || 'Unnamed'}</h1>
                      {!isPlatform && (
                        <button className="character-detail-edit-button" onClick={() => setEditingName(true)} aria-label="Edit character name">
                          <Pencil size={13} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <section className="character-detail-panel-section">
                <div className="character-detail-field-heading">
                  <label className="character-detail-label" htmlFor={editingAppearance ? 'character-appearance' : undefined}>Physical appearance</label>
                  {!isPlatform && !editingAppearance && (
                    <button className="character-detail-edit-link" onClick={() => setEditingAppearance(true)}>
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                </div>
                {!isPlatform && editingAppearance ? (
                  <div className="character-detail-inline-edit character-detail-inline-edit--vertical">
                    <textarea
                      id="character-appearance"
                      className="character-detail-textarea"
                      value={appearanceValue}
                      onChange={(e) => setAppearanceValue(e.target.value)}
                      rows={5}
                      autoFocus
                    />
                    <div className="character-detail-inline-actions">
                      <button className="character-detail-save-btn" onClick={handleSaveAppearance} disabled={isSavingAppearance}>
                        {isSavingAppearance ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save
                      </button>
                      <button className="character-detail-cancel-btn" onClick={() => { setEditingAppearance(false); setAppearanceValue(character.physicalAppearance || ''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <p className={`character-detail-text ${!character.physicalAppearance ? 'character-detail-text--empty' : ''}`}>
                    {character.physicalAppearance || 'No physical appearance description yet.'}
                  </p>
                )}
              </section>

              <section className="character-detail-panel-section">
                <div className="character-detail-field-heading">
                  <label className="character-detail-label" htmlFor={editingPersonality ? 'character-personality' : undefined}>Description</label>
                  {!isPlatform && !editingPersonality && (
                    <button className="character-detail-edit-link" onClick={() => setEditingPersonality(true)}>
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                </div>
                {!isPlatform && editingPersonality ? (
                  <div className="character-detail-inline-edit character-detail-inline-edit--vertical">
                    <textarea
                      id="character-personality"
                      className="character-detail-textarea"
                      value={personalityValue}
                      onChange={(e) => setPersonalityValue(e.target.value)}
                      rows={5}
                      autoFocus
                    />
                    <div className="character-detail-inline-actions">
                      <button className="character-detail-save-btn" onClick={handleSavePersonality} disabled={isSavingPersonality}>
                        {isSavingPersonality ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save
                      </button>
                      <button className="character-detail-cancel-btn" onClick={() => { setEditingPersonality(false); setPersonalityValue(character.personality || ''); }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <p className={`character-detail-text ${!character.personality ? 'character-detail-text--empty' : ''}`}>
                    {character.personality || 'No description yet.'}
                  </p>
                )}
              </section>

              <section className="character-detail-panel-section">
                <div className="character-voice-preview-heading">
                  <Volume2 size={14} />
                  <span>Voice</span>
                </div>
                <div className="character-voice-preview-row">
                  <button
                    type="button"
                    className="character-voice-play-btn"
                    onClick={toggleVoicePreview}
                    disabled={!hasVoicePreview}
                    aria-label={isVoicePlaying ? 'Stop voice preview' : 'Play voice preview'}
                  >
                    {isVoicePlaying ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: 1 }} />}
                  </button>
                  <div className="character-voice-preview-copy">
                    <span className="character-voice-current-name">
                      {character.voiceName || (character.voiceId ? 'Linked voice' : 'No voice linked')}
                    </span>
                    <span className="character-voice-preview-status">
                      {hasVoicePreview ? 'Preview available' : 'Preview unavailable'}
                    </span>
                  </div>
                </div>
              </section>

              {character.keywords && character.keywords.length > 0 && (
                <section className="character-detail-panel-section">
                  <label className="character-detail-label">Keywords</label>
                  <div className="character-detail-keywords">
                    {character.keywords.map((kw, i) => (
                      <span key={`${kw}-${i}`} className="character-detail-keyword">{kw}</span>
                    ))}
                  </div>
                </section>
              )}

              {!isPlatform && (
                <div className="character-detail-danger-zone">
                  {showDeleteConfirm ? (
                    <div className="character-detail-delete-confirm">
                      <div>
                        <strong>Delete this character?</strong>
                        <span>This action cannot be undone.</span>
                      </div>
                      <div className="character-detail-delete-actions">
                        <button className="character-detail-delete-btn" onClick={handleDelete} disabled={isDeleting}>
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                        <button className="character-detail-cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button className="character-detail-delete-trigger" onClick={() => setShowDeleteConfirm(true)}>
                      <Trash2 size={13} /> Delete character
                    </button>
                  )}
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>

      {/* Asset picker modal */}
      {showAssetPicker && (
        <div className="character-modal-overlay" onClick={() => setShowAssetPicker(false)}>
          <div className="character-modal character-asset-picker" onClick={(e) => e.stopPropagation()}>
            <h2 className="character-modal-title">Select Asset</h2>
            {isLoadingAssets && <div className="characters-page-loading">Loading assets...</div>}
            {!isLoadingAssets && orgAssets.length === 0 && (
              <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>No images found. Create one on the Images page first.</p>
            )}
            <div className="character-asset-picker-grid">
              {/* None option */}
              <div
                className={`character-asset-picker-item ${!character.assetId ? 'character-asset-picker-item--selected' : ''}`}
                onClick={() => handleSelectAsset(null)}
              >
                <div className="character-asset-picker-thumb character-asset-picker-thumb--none">
                  <X size={20} />
                </div>
                <span className="character-asset-picker-name">None</span>
              </div>
              {orgAssets.map((a) => (
                <div
                  key={a.id}
                  className={`character-asset-picker-item ${character.assetId === a.id ? 'character-asset-picker-item--selected' : ''}`}
                  onClick={() => handleSelectAsset(a)}
                >
                  <div className="character-asset-picker-thumb">
                    {a.imageUrl ? (
                      <img src={a.imageUrl} alt={a.name} />
                    ) : (
                      <Users size={18} />
                    )}
                  </div>
                  <span className="character-asset-picker-name">{a.name}</span>
                </div>
              ))}
            </div>
            {isSavingAsset && <div className="characters-page-loading">Saving...</div>}
            <div className="character-modal-actions">
              <button className="character-modal-cancel" onClick={() => setShowAssetPicker(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </AuthenticatedLayout>
  );
}

export default CharacterDetailPage;
